/**
 * Open-redirect (CWE-601) guard for post-auth `returnUrl` handling — shared by
 * both apps (admin-web + bidder-web) so the client-side 401→login redirects and
 * the server-side middleware / OAuth redirects can't drift. Historically some
 * checks only rejected absolute URLs (`startsWith('/')`) and left `//evil.com`
 * (protocol-relative) and `/\evil.com` (the IE/Windows quirk that some legacy
 * browsers treat as protocol-relative) wide open.
 *
 * admin-web re-exports these from `@/lib/auth/safe-return-url` for back-compat.
 */

/**
 * True iff `value` is a same-origin relative path safe to use as a redirect
 * target. Rejects absolute URLs, protocol-relative URLs (`//evil.com`), and
 * the IE/Windows quirk (`/\evil.com`).
 *
 * The input is assumed to already be URL-decoded — both `useSearchParams()`
 * and `URL.searchParams.get(...)` return decoded values. Don't pre-decode
 * before calling this.
 */
export function isSafeReturnUrl(value: string | null | undefined): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.startsWith('/\\')) return false;
  return true;
}

/**
 * Returns `value` if it's a safe same-origin relative path, otherwise `/`.
 */
export function safeReturnUrl(value: string | null | undefined): string {
  return isSafeReturnUrl(value) ? value : '/';
}
